import React from 'react';
import {
  withSystem,
  TypeImplementation,
  withMeta,
  setType,
  Expressions,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionAction,
  updateExpressionAtAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {
  TRecord,
  TTypeConstructor,
  EText,
  EHole,
  ERecord,
  TText,
} from '@packages/systems/wfdl-ast';
import {pluginName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  styledDiv,
  styled,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {Subheading} from '@packages/systems/design-system/Text';
import {slugifyLive} from '@packages/systems/core/utils/RecordUtils';
import {LogicLabel} from '@packages/systems/flows/shared';
import TooltipControl from '@packages/systems/design-system/TooltipControl';
import {StyledHelpIcon} from '@packages/systems/plugins/Effect';

const fieldsType = TRecord({
  name: withSystem(
    {
      isFullWidth: true,
      label: 'Name',
      labelVariant: 'hidden',
      bindUIVariant: 'custom',
    },
    withMeta({placeholder: 'Enter name...'}, TText)
  ),
  slug: withSystem(
    {
      isFullWidth: true,
      label: 'Slug',
      labelVariant: 'hidden',
      bindUIVariant: 'custom',
    },
    TTypeConstructor(qualify(pluginName('CMS'), typeName('Slug')))
  ),
});

const typeAsSlugType = (expr: ContextualExpression) => {
  return setType(
    expr,
    withSystem(
      {
        isFullWidth: true,
        label: 'Slug',
        labelVariant: 'hidden',
        bindUIVariant: 'custom',
      },
      TTypeConstructor(qualify(pluginName('CMS'), typeName('Slug')))
    )
  );
};

const typeAsNameType = (expr: ContextualExpression) => {
  return setType(
    expr,
    withSystem(
      {
        isFullWidth: true,
        label: 'Name',
        labelVariant: 'hidden',
      },
      withMeta({placeholder: 'Enter name...'}, TText)
    )
  );
};

const slugPath: ExpressionPath = [{in: 'Record', at: 'slug'}];

const getPaths = (annExpr: ContextualExpression) => {
  const rec = ContextualExpressions.getRecord(stripAnn(annExpr));
  const nameExpr = rec?.name;
  const slugExpr = rec?.slug;

  if (nameExpr == null || slugExpr == null) {
    return null;
  }

  return {
    nameExpr,
    slugExpr,
  };
};

const GridWrapper = emotionStyled('div')({
  display: 'grid',
  gridRowGap: tokens.space[2],
});

const LabelWrapper = styledDiv(({theme: {gutters}}) => ({
  display: 'grid',
  gridAutoColumns: 'minmax(0, auto)',
  gridRowGap: gutters.x4,
}));

const Divider = styledDiv(({theme: {gutters, colors}}) => ({
  height: 1,
  backgroundColor: colors.backgroundDeep,
  width: `calc(100% + ${gutters.x8 * 2 + gutters.x4}px)`,
  marginLeft: -gutters.x8 - gutters.x4 / 2,
}));

const StyledSlugDescription = styledDiv(({theme: {colors, fontSizes}}) => ({
  color: colors.foregroundSlightlyDim,
  fontSize: fontSizes.copy,
}));

const StyledSlugLabel = styled(LogicLabel)(({theme: {gutters}}) => ({
  display: 'grid',
  gridTemplateColumns: `max-content ${gutters.x16}px`,
  gridColumnGap: gutters.x4,
  alignItems: 'center',
}));

const TooltipContent = styledDiv(({theme: {gutters}}) => ({
  maxWidth: gutters.x4 * 56, // 224
}));

const justifyCenter = {justifySelf: 'center'} as const;

/**
 * The "name" and "slug" fields in the Create CMS item block
 */
export const BasicInfoFieldsType = TypeImplementation.Alias({
  type: fieldsType,
  render: function RenderBasicFields({expr, dispatch}) {
    const paths = React.useMemo(() => getPaths(expr), [expr]);

    const onNameChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
      (action) => {
        // We need to provide the full record if we have a hole
        if (
          action.type === 'EXPRESSION_BIND_CHANGED' &&
          action.binding &&
          ContextualExpressions.isHole(stripAnn(expr))
        ) {
          return dispatch(
            updateExpressionAction(
              ERecord({
                name: action.binding,
                slug: action.binding,
              })
            )
          );
        }

        const text = Expressions.getText(action.payload.newExpression);

        // If the user has erased the "name" input, update "name" to EHole and slug to empty text
        if (text != null && text.length === 0) {
          return dispatch(
            updateExpressionAction(
              ERecord({
                name: EHole(),
                slug: EText(''),
              })
            )
          );
        }

        // Otherwise update the name, and update the slug to reflect the name
        if (text != null && text.length > 0) {
          return dispatch(
            updateExpressionAction(
              ERecord({
                name: EText(text),
                slug: EText(slugifyLive(text.trim())),
              })
            )
          );
        }

        // Fall back to any changes to "name" is reflected in Slug
        dispatch(
          updateExpressionAction(
            ERecord({
              name: action.payload.newExpression,
              slug: action.payload.newExpression,
            })
          )
        );
      },
      [dispatch, expr]
    );

    const onSlugChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'action' implicitly has an 'any' type.
      (action) => {
        // If the user is not changing bindings
        if (action.type === 'EXPRESSION_ACTION' && !action.key.length) {
          const text = Expressions.getText(action.payload.newExpression);
          // If the user has erased the "slug" input, update it to empty text
          if (text !== null && text.length === 0) {
            return dispatch(updateExpressionAtAction(EText(''), slugPath));
          }
        }

        // We need to provide the full record if we have a hole
        if (
          ContextualExpressions.isHole(stripAnn(expr)) &&
          (action.type === 'EXPRESSION_ACTION' ||
            action.type === 'EXPRESSION_BIND_CHANGED')
        ) {
          dispatch(
            updateExpressionAction(
              ERecord({
                name: EHole(),
                slug:
                  action.type === 'EXPRESSION_ACTION'
                    ? action.payload.newExpression
                    : action.binding,
              })
            )
          );
          return;
        }

        // Fall back to the normal dispatching process
        return extendExpressionEditorActionAtPath(dispatch)(slugPath)(action);
      },
      [dispatch, expr]
    );
    const nameExpr = paths ? paths.nameExpr : null;

    const __HACK__nameKey = React.useMemo(() => {
      // This hack exists to support making sure `Slug` shares the same value as `Name`
      // when `Name` input is updated. The reason we're triggering an entire remount is
      // because SlateJS doesn't easily support controlled component (where
      // all we'd theoretically do is update the value externally) without causing bugs.
      // With this hack, we'd only isolate the hack to this specific area, instead of affecting all
      // SlateJS inputs (via InterpolatedTextInput.jsx)..
      return nameExpr
        ? JSON.stringify(stripAll(nameExpr))
        : 'CMS_NAME_SLUG_KEY';
    }, [nameExpr]);

    return (
      <GridWrapper>
        <Divider />
        <Subheading>Basic info</Subheading>
        <LabelWrapper>
          <LogicLabel required label="Name">
            <ExpressionEditor
              dispatch={onNameChange}
              expression={paths ? paths.nameExpr : typeAsNameType(expr)}
            />
          </LogicLabel>
        </LabelWrapper>
        <LabelWrapper>
          <StyledSlugLabel label="Slug">
            {/* @ts-expect-error - TS2739 - Type '{ style: { readonly justifySelf: "center"; }; icon: Element; content: Element; alternate: true; }' is missing the following properties from type '{ [x: string]: any; iconStyle?: { padding: number; boxSizing: string; } | undefined; icon?: Element | undefined; cursor?: string | undefined; placement?: string | undefined; children: any; hideOnClick: any; style: any; automationLabel?: string | undefined; }': children, hideOnClick */}
            <TooltipControl
              style={justifyCenter}
              icon={<StyledHelpIcon style={{marginLeft: '4px'}} />}
              content={
                <TooltipContent>
                  Add a slug for this item’s URL. If not, the URL will be
                  automatically created using the item name.
                </TooltipContent>
              }
              alternate
            />
            <StyledSlugDescription>
              To change the item URL, edit the slug.
            </StyledSlugDescription>
            <ExpressionEditor
              key={__HACK__nameKey}
              dispatch={onSlugChange}
              expression={paths ? paths.slugExpr : typeAsSlugType(expr)}
            />
          </StyledSlugLabel>
        </LabelWrapper>
        <Divider />
      </GridWrapper>
    );
  },
});
